import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Filter, TrendingUp, Clock, Star, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import VideoCard from './VideoCard';
import { supabase } from '../../lib/supabase';
import { useSupabase } from '../../contexts/SupabaseContext';

interface Video {
  id: string;
  title: string;
  video_url: string;
  thumbnail_url: string | null;
  tags: string[];
  product_links: any[];
  view_count: number;
  created_at: string;
  seller: {
    id: string;
    organization: string;
    location: string;
    verified: boolean;
  };
  reactions: {
    interested_count: number;
    not_interested_count: number;
    user_reaction: string | null;
  };
  comments_count: number;
}

const dummyVideos: Video[] = [
  {
    id: 'video-1',
    title: 'Traditional Bangladeshi Textile Weaving',
    video_url: 'https://your-supabase-url.supabase.co/storage/v1/object/public/videos/video1.mp4',
    thumbnail_url: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
    tags: ['weaving', 'traditional', 'textiles', 'handmade'],
    product_links: [
      { name: 'Handwoven Fabric', url: '/product/1' },
      { name: 'Traditional Textile', url: '/product/2' }
    ],
    view_count: 2150,
    created_at: '2024-01-15T00:00:00Z',
    seller: {
      id: '1',
      organization: 'Heritage Weavers',
      location: 'Dhaka, Bangladesh',
      verified: true
    },
    reactions: {
      interested_count: 156,
      not_interested_count: 8,
      user_reaction: null
    },
    comments_count: 42
  },
  {
    id: 'video-2',
    title: 'Modern Garment Manufacturing in Bangladesh',
    video_url: 'https://your-supabase-url.supabase.co/storage/v1/object/public/videos/video2.mp4',
    thumbnail_url: 'https://images.pexels.com/photos/5217911/pexels-photo-5217911.jpeg',
    tags: ['garments', 'manufacturing', 'modern', 'export'],
    product_links: [
      { name: 'Export Quality Shirt', url: '/product/3' },
      { name: 'Premium Garments', url: '/product/4' }
    ],
    view_count: 1890,
    created_at: '2024-01-20T00:00:00Z',
    seller: {
      id: '2',
      organization: 'Bengal Textiles Ltd.',
      location: 'Dhaka, Bangladesh',
      verified: true
    },
    reactions: {
      interested_count: 134,
      not_interested_count: 12,
      user_reaction: null
    },
    comments_count: 28
  },
  {
    id: 'video-3',
    title: 'Sustainable Jute Product Manufacturing',
    video_url: 'https://your-supabase-url.supabase.co/storage/v1/object/public/videos/video3.mp4',
    thumbnail_url: 'https://images.pexels.com/photos/7148621/pexels-photo-7148621.jpeg',
    tags: ['jute', 'sustainable', 'eco-friendly', 'export'],
    product_links: [
      { name: 'Eco Jute Bags', url: '/product/5' },
      { name: 'Sustainable Products', url: '/product/6' }
    ],
    view_count: 1650,
    created_at: '2024-01-25T00:00:00Z',
    seller: {
      id: '3',
      organization: 'Eco Jute Co.',
      location: 'Khulna, Bangladesh',
      verified: true
    },
    reactions: {
      interested_count: 98,
      not_interested_count: 6,
      user_reaction: null
    },
    comments_count: 35
  },
  {
    id: 'video-4',
    title: 'Leather Craftsmanship - Wallet Making Process',
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
    thumbnail_url: 'https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg',
    tags: ['leather', 'craftsmanship', 'wallet', 'handmade'],
    product_links: [
      { name: 'Premium Leather Wallet', url: '/product/6' },
      { name: 'Leather Card Holder', url: '/product/7' }
    ],
    view_count: 756,
    created_at: '2024-02-01T00:00:00Z',
    seller: {
      id: '4',
      organization: 'Dhaka Leather Works',
      location: 'Dhaka, Bangladesh',
      verified: true
    },
    reactions: {
      interested_count: 45,
      not_interested_count: 3,
      user_reaction: null
    },
    comments_count: 18
  },
  {
    id: 'video-5',
    title: 'Fresh Hilsa Fish Processing for Export',
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
    thumbnail_url: 'https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg',
    tags: ['hilsa', 'fish', 'processing', 'export', 'seafood'],
    product_links: [
      { name: 'Frozen Hilsa Fish', url: '/product/8' }
    ],
    view_count: 1340,
    created_at: '2024-02-05T00:00:00Z',
    seller: {
      id: '5',
      organization: 'Bengal Fisheries',
      location: 'Chittagong, Bangladesh',
      verified: true
    },
    reactions: {
      interested_count: 78,
      not_interested_count: 7,
      user_reaction: null
    },
    comments_count: 29
  },
  {
    id: 'video-6',
    title: 'Sustainable Textile Production Methods',
    video_url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
    thumbnail_url: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
    tags: ['sustainable', 'textile', 'eco-friendly', 'production'],
    product_links: [
      { name: 'Organic Cotton Shirt', url: '/product/9' },
      { name: 'Eco-Friendly Fabric', url: '/product/10' }
    ],
    view_count: 945,
    created_at: '2024-02-08T00:00:00Z',
    seller: {
      id: '6',
      organization: 'Cotton Craft BD',
      location: 'Gazipur, Bangladesh',
      verified: true
    },
    reactions: {
      interested_count: 62,
      not_interested_count: 4,
      user_reaction: null
    },
    comments_count: 21
  }
];

const VideoFeed: React.FC = () => {
  const { user } = useSupabase();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sortBy, setSortBy] = useState<'newest' | 'most_interested' | 'recommended'>('newest');
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  const sliderRef = useRef<HTMLDivElement>(null);
  const videosPerSlide = isMobile ? 1 : 3; // Show 1 video on mobile, 3 on desktop

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Fetch videos
  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      
      // Try to fetch from database first
      const { data: videosData, error: videosError } = await supabase
        .from('videos')
        .select(`
          id,
          title,
          video_url,
          thumbnail_url,
          tags,
          product_links,
          view_count,
          created_at,
          suppliers:seller_id (
            id,
            organization,
            location,
            verified
          )
        `)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });
      
      let finalVideos = [];
      
      if (videosError || !videosData || videosData.length === 0) {
        // Use dummy data if no real videos or error
        finalVideos = dummyVideos;
      } else {
        // Use real data and add dummy data if needed
        const videosWithMetadata = await Promise.all(
          videosData.map(async (video) => {
            // Get reaction counts
            const { data: reactionData } = await supabase
              .rpc('get_video_reaction_counts', { video_uuid: video.id });
            
            // Get user's reaction if logged in
            let userReaction = null;
            if (user) {
              const { data: userReactionData } = await supabase
                .from('video_reactions')
                .select('reaction_type')
                .eq('video_id', video.id)
                .eq('user_id', user.id)
                .maybeSingle();
              
              userReaction = userReactionData?.reaction_type || null;
            }
            
            // Get comments count
            const { count: commentsCount } = await supabase
              .from('video_comments')
              .select('*', { count: 'exact', head: true })
              .eq('video_id', video.id);

            return {
              ...video,
              seller: video.suppliers,
              reactions: {
                interested_count: reactionData?.[0]?.interested_count || 0,
                not_interested_count: reactionData?.[0]?.not_interested_count || 0,
                user_reaction: userReaction
              },
              comments_count: commentsCount || 0
            };
          })
        );
        
        // Combine real videos with dummy videos for demo
        finalVideos = videosWithMetadata.length > 0 ? [...videosWithMetadata, ...dummyVideos] : dummyVideos;
      }

      // Apply sorting
      switch (sortBy) {
        case 'newest':
          finalVideos.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          break;
        case 'most_interested':
          finalVideos.sort((a, b) => b.reactions.interested_count - a.reactions.interested_count);
          break;
        case 'recommended':
          finalVideos.sort((a, b) => b.view_count - a.view_count);
          break;
      }
      
      setVideos(finalVideos);
      
    } catch (err) {
      console.error('Error fetching videos:', err);
      // Use dummy data as fallback
      setVideos(dummyVideos);
    } finally {
      setLoading(false);
    }
  }, [sortBy, user]);

  // Initial fetch
  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Slider navigation
  const totalSlides = Math.ceil(videos.length / videosPerSlide);
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };
  
  // Auto-slide every 10 seconds
  useEffect(() => {
    if (videos.length > videosPerSlide) {
      const interval = setInterval(nextSlide, 10000);
      return () => clearInterval(interval);
    }
  }, [videos.length, videosPerSlide, isMobile]);

  // Touch handlers for mobile swiping
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && videos.length > videosPerSlide) {
      nextSlide();
    }
    if (isRightSwipe && videos.length > videosPerSlide) {
      prevSlide();
    }
  };
  // Handle reactions
  const handleReaction = async (videoId: string, reactionType: 'interested' | 'not_interested') => {
    if (!user) return;
    
    try {
      // Check if user already reacted
      const { data: existingReaction } = await supabase
        .from('video_reactions')
        .select('reaction_type')
        .eq('video_id', videoId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (existingReaction) {
        if (existingReaction.reaction_type === reactionType) {
          // Remove reaction if clicking the same one
          await supabase
            .from('video_reactions')
            .delete()
            .eq('video_id', videoId)
            .eq('user_id', user.id);
        } else {
          // Update reaction if clicking different one
          await supabase
            .from('video_reactions')
            .update({ reaction_type: reactionType })
            .eq('video_id', videoId)
            .eq('user_id', user.id);
        }
      } else {
        // Insert new reaction
        await supabase
          .from('video_reactions')
          .insert({
            video_id: videoId,
            user_id: user.id,
            reaction_type: reactionType
          });
      }
      
      // Refresh videos to update counts
      // For dummy data, just update local state
      setVideos(prevVideos => 
        prevVideos.map(v => 
          v.id === videoId 
            ? {
                ...v,
                reactions: {
                  ...v.reactions,
                  user_reaction: existingReaction?.reaction_type === reactionType ? null : reactionType,
                  interested_count: reactionType === 'interested' 
                    ? (existingReaction?.reaction_type === 'interested' ? v.reactions.interested_count - 1 : v.reactions.interested_count + 1)
                    : v.reactions.interested_count,
                  not_interested_count: reactionType === 'not_interested'
                    ? (existingReaction?.reaction_type === 'not_interested' ? v.reactions.not_interested_count - 1 : v.reactions.not_interested_count + 1)
                    : v.reactions.not_interested_count
                }
              }
            : v
        )
      );
      
    } catch (error) {
      console.error('Error handling reaction:', error);
    }
  };

  // Handle comments (placeholder)
  const handleComment = (videoId: string) => {
    // This would open a comments modal or navigate to a comments page
    console.log('Open comments for video:', videoId);
  };

  if (loading && videos.length === 0) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading videos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchVideos}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No videos available yet</p>
          <p className="text-sm text-gray-500">Check back later for new content from our suppliers</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Controls */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-gray-900">Supplier Showcase</h2>
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-orange-500"
            >
              <option value="newest">Newest</option>
              <option value="most_interested">Most Interested</option>
              <option value="recommended">Recommended</option>
            </select>
          </div>
        </div>
        
        <div className="flex items-center space-x-4 text-sm text-gray-500">
          {videos.length} video{videos.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Video Slider */}
      <div className="relative">
        {/* Navigation Buttons */}
        {videos.length > videosPerSlide && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white transition-all duration-200 hover:scale-110"
            >
              <ChevronLeft className="h-6 w-6 text-gray-700" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white transition-all duration-200 hover:scale-110"
            >
              <ChevronRight className="h-6 w-6 text-gray-700" />
            </button>
          </>
        )}
        
        {/* Video Slider Container */}
        <div 
          ref={sliderRef}
          className="overflow-hidden rounded-xl"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div 
            className="flex transition-transform duration-500 ease-in-out"
            style={{ 
              transform: `translateX(-${currentSlide * 100}%)`,
              width: `${totalSlides * 100}%`
            }}
          >
            {Array.from({ length: totalSlides }).map((_, slideIndex) => (
              <div 
                key={slideIndex}
                className={`flex ${isMobile ? 'space-x-0' : 'space-x-4'} px-2`}
                style={{ width: `${100 / totalSlides}%` }}
              >
                {videos
                  .slice(slideIndex * videosPerSlide, (slideIndex + 1) * videosPerSlide)
                  .map((video, videoIndex) => (
                    <div 
                      key={video.id}
                      className={`flex-1 ${isMobile ? 'h-[600px]' : 'h-[500px]'}`}
                    >
                      <VideoCard
                        video={video}
                        isVisible={slideIndex === currentSlide}
                        isMobile={isMobile}
                        onReaction={handleReaction}
                        onComment={handleComment}
                      />
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>
        
        {/* Slide Indicators */}
        {videos.length > videosPerSlide && (
          <div className="flex justify-center mt-6 space-x-2">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  currentSlide === index 
                    ? 'bg-orange-600 scale-125' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoFeed;